/**
 * @file src/config/audioPerformance.js
 * @description Audio-focused performance presets plus helpers for persisting the user's selection.
 */
import {
  THROTTLE_LOW_MS,
  THROTTLE_MID_MS,
  THROTTLE_HIGH_MS,
} from './Constants.js';

/**
 * Audio performance presets control decoding format, DSP quality knobs, and
 * the cadence at which we emit feedback to external systems (MIDI, sensors).
 * Throttle timing is kept alongside each preset so UI selections can update
 * both fidelity and cadence in one step.
 *
 * `audioContextLatencyHint` is the one field here that is NOT live: the browser
 * sizes the device callback when the AudioContext is constructed and never
 * re-reads the hint, so this value is applied once at boot and a change takes
 * effect on the next load. It ladders with the rest of the preset — a bigger
 * buffer means fewer, longer audio callbacks, which is the cheapest headroom
 * available on a slow machine, paid for in output latency that sensor and knob
 * play can feel. So the low preset buys the headroom and the high preset keeps
 * the response.
 */
export const AUDIO_PERFORMANCE_PRESETS = {
  LOW: {
    key: 'LOW',
    label: 'low',
    assetFormat: 'mp3',
    audioContextLatencyHint: 'playback',
    effectQuality: {
      oversampling: 'none',
      convolutionIRQuality: 'low',
      fftSize: 512,
      interpMode: 'linear',
      filterModel: 'basic',
      modSmoothingMs: 60,
    },
    safeRampTimeMs: 60,
    feedbackThrottleMs: THROTTLE_HIGH_MS, // 50ms = ~20fps (lower performance = slower updates)
  },
  MID: {
    key: 'MID',
    label: 'mid',
    assetFormat: 'mp3',
    audioContextLatencyHint: 'balanced',
    effectQuality: {
      oversampling: 'none',
      convolutionIRQuality: 'mid',
      fftSize: 1024,
      interpMode: 'linear',
      filterModel: 'hq',
      modSmoothingMs: 45,
    },
    safeRampTimeMs: 45,
    feedbackThrottleMs: THROTTLE_MID_MS, // 33ms = ~30fps (balanced)
  },
  HIGH: {
    key: 'HIGH',
    label: 'high',
    assetFormat: 'pcm',
    audioContextLatencyHint: 'interactive',
    effectQuality: {
      oversampling: 'none',
      convolutionIRQuality: 'high',
      fftSize: 2048,
      interpMode: 'cubic',
      filterModel: 'hq',
      modSmoothingMs: 30,
    },
    safeRampTimeMs: 30,
    feedbackThrottleMs: THROTTLE_LOW_MS, // 16ms = ~60fps (higher performance = faster updates)
  },
};

const PRESET_ALIAS = {
  low: 'LOW',
  LOW: 'LOW',
  mid: 'MID',
  MID: 'MID',
  high: 'HIGH',
  HIGH: 'HIGH',
};

const STORAGE_KEY = 'audioProfile';

/**
 * Accepted spellings of the audio buffer preference, mapped to Web Audio `latencyHint` values.
 *
 * The three `latencyHint` names map to THEMSELVES, and that is the point: the setting
 * is stored and shown under the API's own words rather than translated into ours. A buffer size in
 * samples is deliberately not accepted — the spec allows a numeric hint but no engine promises to
 * honour it, so a number here would be one we cannot deliver.
 *
 * `low|mid|high` is the shorthand, and it names the BUFFER SIZE the way every audio application
 * does: low is a small buffer, high is a large one. So `low` is `interactive` and `high` is
 * `playback`.
 *
 * It reads the opposite way round from `?audio=low|mid|high`, and that is correct rather than a
 * clash: the two params name different quantities. `audio` picks how good the sound is, where low
 * is the cheap end; this picks how big a chunk the machine works on, where low is the small,
 * responsive, expensive end. Low quality and a low buffer are genuinely opposite requests.
 */
const AUDIO_BUFFER_ALIAS = {
  interactive: 'interactive',
  balanced: 'balanced',
  playback: 'playback',
  low: 'interactive',
  LOW: 'interactive',
  mid: 'balanced',
  MID: 'balanced',
  high: 'playback',
  HIGH: 'playback',
};

/** `auto`, and anything unrecognised, resolve to null — meaning "let the quality preset decide". */
function normalizeAudioBufferParam(value) {
  if (!value) return null;
  return AUDIO_BUFFER_ALIAS[String(value).trim()] ?? null;
}

function normalizeKey(value) {
  if (!value) return null;
  return PRESET_ALIAS[String(value).trim()] ?? null;
}

/**
 * Cookies the website writes on the shared parent domain (`.plantasia.space`) so that an orbiter
 * opened by its OWN link — not launched from root — still gets the person's chosen audio settings.
 *
 * A cookie rather than the account, and this is the one place it genuinely matters: the browser
 * sizes the audio buffer when the audio engine is built, which here happens at import, and never
 * re-reads the hint. An account fetch resolves milliseconds too late to influence that, so the
 * setting would silently only apply on the NEXT load. A cookie is already in the document when the
 * first line runs.
 */
const AUDIO_QUALITY_COOKIE = 'audioQuality';
const AUDIO_BUFFER_COOKIE = 'audioBuffer';

function tryReadStorage(key = STORAGE_KEY) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

/** Read one cookie by name. Returns null when absent, unreadable, or empty. */
function tryReadCookie(name) {
  try {
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') return null;
    const prefix = `${name}=`;
    for (const part of document.cookie.split(';')) {
      const entry = part.trim();
      if (!entry.startsWith(prefix)) continue;
      const value = decodeURIComponent(entry.slice(prefix.length));
      return value === '' ? null : value;
    }
    return null;
  } catch (_) {
    return null;
  }
}

function toUrlSearchParams(params) {
  if (params instanceof URLSearchParams) return params;
  if (typeof params === 'string') return new URLSearchParams(params);
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location?.search ?? '');
  }
  return new URLSearchParams('');
}

/**
 * The buffer laid over whichever quality preset wins, and who chose it: **URL → cookie → the
 * preset's own**.
 *
 * There is deliberately no localStorage tier, unlike the quality preset above. Nothing in this app
 * ever writes a buffer choice to storage — the control lives on the website, and its cookie is what
 * crosses over — so a storage tier would be a branch that can never be taken, and a documented
 * fallback that quietly lies about how the value is resolved.
 *
 * `auto`, absent and unrecognised all give a null hint, meaning "keep the preset's own", i.e. the
 * behaviour that predates the setting.
 *
 * Shared by `resolveAudioPerformancePreset` and `getAudioPerformancePresetByKey` on purpose. Only
 * the first used to apply it, so a preset built the second way — which is what happens whenever a
 * card's descriptor names a different `audioProfile` — carried the preset's own hint while the
 * context was running the person's chosen buffer. That is not a cosmetic disagreement: the adapter
 * compares the two and warns that the profile "asks for" a buffer it cannot have, so a saved
 * preference produced a warning about itself on every such load.
 *
 * @param {URLSearchParams} [search]
 * @returns {{ hint: string|null, source: 'url'|'cookie'|'preset' }}
 */
function resolveAudioBufferOverride(search) {
  const params = search ?? toUrlSearchParams(undefined);

  const fromUrl = normalizeAudioBufferParam(params.get('audioBuffer'));
  if (fromUrl) return { hint: fromUrl, source: 'url' };

  const fromCookie = normalizeAudioBufferParam(tryReadCookie(AUDIO_BUFFER_COOKIE));
  if (fromCookie) return { hint: fromCookie, source: 'cookie' };

  return { hint: null, source: 'preset' };
}

export function resolveAdaptiveAudioMode(params) {
  void params;
  // Adaptive mode is always-on in current product strategy.
  // URL/storage mode flags are intentionally ignored to reduce mode complexity.
  return true;
}

/**
 * Resolves the active audio performance preset, and the audio buffer laid on top of it.
 *
 * The QUALITY settles **URL → cookie → this device's localStorage → the default**, and the order is
 * a hand-off rather than four competing truths. The URL is a deliberate per-link override (testing,
 * or an embed that states its own quality). The cookie is how the website's setting reaches the
 * orbiter — including one opened by its own link, which is the case that used to silently drift:
 * before, this function saw only the URL and localStorage, so the same person got different audio
 * depending on how they arrived. localStorage remains last so a device that made its own choice,
 * and a guest who never met the website, still work.
 *
 * The BUFFER settles **URL → cookie → the preset's own**, with no storage tier — see
 * `resolveAudioBufferOverride` for why that branch would be unreachable.
 *
 * Quality and buffer are resolved INDEPENDENTLY. Quality picks the file format and effect detail;
 * the buffer picks how the browser schedules the work. Welding them together is what made "good
 * sound AND a stable buffer on an old laptop" impossible — so an explicit buffer is applied on top
 * of whichever preset won, and `auto` (or nothing) leaves the preset's own hint in place.
 *
 * @param {URLSearchParams|string|undefined} params
 * @returns {{ key: 'LOW'|'MID'|'HIGH', preset: typeof AUDIO_PERFORMANCE_PRESETS.HIGH, source: string, bufferSource: 'url'|'cookie'|'preset' }}
 */
export function resolveAudioPerformancePreset(params) {
  const search = toUrlSearchParams(params);
  const adaptiveAudioMode = resolveAdaptiveAudioMode(search);

  const { hint: bufferOverride, source: bufferSource } = resolveAudioBufferOverride(search);

  const applyOverrides = (preset) => {
    const result = { ...preset, adaptiveAudioMode };
    if (bufferOverride) {
      result.audioContextLatencyHint = bufferOverride;
    }
    return result;
  };

  const resolved = (key, source) => ({
    key,
    preset: applyOverrides(AUDIO_PERFORMANCE_PRESETS[key]),
    source,
    bufferSource,
  });

  const fromUrl = normalizeKey(search.get('audio'));
  if (fromUrl && AUDIO_PERFORMANCE_PRESETS[fromUrl]) return resolved(fromUrl, 'audio');

  const fromCookie = normalizeKey(tryReadCookie(AUDIO_QUALITY_COOKIE));
  if (fromCookie && AUDIO_PERFORMANCE_PRESETS[fromCookie]) return resolved(fromCookie, 'cookie');

  const fromStorage = normalizeKey(tryReadStorage());
  if (fromStorage && AUDIO_PERFORMANCE_PRESETS[fromStorage]) return resolved(fromStorage, 'storage');

  return resolved('MID', 'default');
}

export function getAudioPerformancePresetByKey(key) {
  const preset = AUDIO_PERFORMANCE_PRESETS[key] ?? AUDIO_PERFORMANCE_PRESETS.MID;
  const { hint } = resolveAudioBufferOverride();
  return {
    ...preset,
    // The chosen buffer wins here exactly as it does in `resolveAudioPerformancePreset`. The context
    // is built ONCE at boot, so a preset that named a different hint would be describing a buffer
    // that does not exist.
    ...(hint ? { audioContextLatencyHint: hint } : null),
    adaptiveAudioMode: resolveAdaptiveAudioMode(),
  };
}

export function normalizeAudioPerformanceKey(value) {
  return normalizeKey(value);
}

/**
 * Returns the throttle duration (ms) that should be used for external feedback
 * when the provided audio preset is active.
 * @param {string|null|undefined} value
 * @returns {number}
 */
export function getAudioPerformanceThrottleMs(value) {
  const key = normalizeKey(value) ?? 'MID';
  const fallback = key === 'LOW' ? THROTTLE_HIGH_MS : key === 'HIGH' ? THROTTLE_LOW_MS : THROTTLE_MID_MS;
  const preset = AUDIO_PERFORMANCE_PRESETS[key];
  return Number(preset?.feedbackThrottleMs) || fallback;
}

/**
 * Remember the audio quality this device is running, so a reload keeps it.
 *
 * @param {string} key
 * @param {{ from?: string }} [opts] where the key came from. A key the COOKIE decided is not this
 *   device's choice — it is the account's, arriving from the website — so writing it here would
 *   overwrite whatever this device had chosen for itself, on every single load, and leave that
 *   choice unrecoverable if the cookie were later cleared. The cookie is already consulted ahead of
 *   storage on the next load, so there is nothing to gain by copying it down either.
 */
export function persistAudioPerformanceKey(key, { from } = {}) {
  if (from === 'cookie') return;
  const normalized = normalizeKey(key);
  if (!normalized) return;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, normalized);
  } catch (_) {
    // ignore persistence errors
  }
}
